import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './Table';
import type { RowState } from './useRowAutosave';

// Celdas editables para las grillas del admin.
//
// Criterio de diseño: en reposo se ven como TEXTO (sin cajas ni bordes), porque una
// tabla llena de inputs con borde es ilegible. El borde aparece al pasar el mouse y el
// foco se marca con el color de marca. Así la grilla se lee como una tabla y se edita
// como un formulario, sin modo "editar".
//
// Siempre se usan junto a useRowAutosave: no hay botón de guardar.

// Medidas y foco del `input` de uiarc (src/ui/arc/input.module.css): alto de control,
// radio de control y el foco se marca con el borde, sin anillo.
const base = 'h-8 w-full min-w-0 rounded-[var(--radius-control)] border border-transparent bg-transparent px-2 '
    + 'text-[length:var(--text-sm)] text-ink transition-colors placeholder:text-faint '
    + 'hover:border-line focus:border-[var(--foreground)] focus:bg-card focus:outline-none';

export function TextCell({
    value, onChange, placeholder, className, align, autoFocus, onBlur,
}: {
    value: string | null | undefined;
    onChange: (v: string) => void;
    placeholder?: string;
    className?: string;
    align?: 'left' | 'right';
    /** Abre con el foco puesto y el texto seleccionado (ej. recién duplicado: escribís y reemplaza). */
    autoFocus?: boolean;
    onBlur?: () => void;
}) {
    return (
        <input
            value={value ?? ''}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onFocus={autoFocus ? e => e.currentTarget.select() : undefined}
            onBlur={onBlur}
            onKeyDown={onBlur ? e => { if (e.key === 'Enter') e.currentTarget.blur(); } : undefined}
            onChange={e => onChange(e.target.value)}
            className={cn(base, align === 'right' && 'text-right tabular-nums', className)}
        />
    );
}

export function NumberCell({
    value, onChange, placeholder, className, prefix, min = 0, grouped = false,
}: {
    value: number | string | null | undefined;
    onChange: (v: string) => void;
    placeholder?: string;
    className?: string;
    prefix?: string;
    min?: number;
    /** En reposo muestra separador de miles (2.500.000); al enfocar, el número crudo para editar. */
    grouped?: boolean;
}) {
    const [focused, setFocused] = React.useState(false);
    const n = Number(value);
    const pretty = grouped && !focused && value !== '' && value != null && Number.isFinite(n);
    return (
        <span className="inline-flex w-full items-center justify-end gap-0.5">
            {prefix && <span className="shrink-0 text-[length:var(--text-xs)] text-faint">{prefix}</span>}
            <input
                type={pretty ? 'text' : 'number'} inputMode="decimal" min={min}
                value={pretty ? n.toLocaleString('es-AR', { maximumFractionDigits: 2 }) : (value ?? '')}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
                className={cn(base, 'text-right tabular-nums', className)}
            />
        </span>
    );
}

export function SelectCell({
    value, onChange, options, className, title,
}: {
    value: string | null | undefined;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
    className?: string;
    title?: string;
}) {
    return (
        // Sin la flecha nativa (en una grilla se repite en cada fila y es puro ruido): el
        // chevron aparece al pasar por la fila (`group` del <tr> del DataGrid) o con foco.
        <span className={cn('relative inline-flex w-full items-center', className)}>
            <select
                value={value ?? ''}
                title={title}
                onChange={e => onChange(e.target.value)}
                className={cn(base, 'peer cursor-pointer appearance-none truncate pr-6 text-soft')}>
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown size={14} aria-hidden="true"
                className="pointer-events-none absolute right-1.5 text-faint opacity-0 transition-opacity group-hover:opacity-100 peer-focus:opacity-100" />
        </span>
    );
}

/** Indicador del autoguardado. Ocupa ancho fijo para que la fila no salte al aparecer. */
export function SaveState({ state }: { state?: RowState }) {
    return (
        <span className="inline-block w-[4.6rem] shrink-0 text-[length:var(--text-xs)] font-medium">
            {state === 'saving' && <span className="text-faint">guardando…</span>}
            {state === 'saved' && <span className="text-[var(--success)]">✓ guardado</span>}
            {state === 'error' && <span className="text-[var(--danger)]">✗ error</span>}
        </span>
    );
}
